import { AnnotationFeedbackDetail } from 'api';
import { useCallback, useState } from 'react';
import { Box } from 'ui';
import { ChatBubble } from 'ui/icons';

import { CommentBubble } from '../CommentBubble/CommentBubble';
import { PendingComment, ScaledComment } from '../SegmentationOverlay/SegmentationOverlay';
import { SpeechBubbleAvatar } from '../SpeechBubbleAvatar/SpeechBubbleAvatar';

const isAnnotationFeedbackDetail = (
  comment: AnnotationFeedbackDetail | PendingComment
): comment is AnnotationFeedbackDetail => 'author' in comment && comment.author !== undefined;

export const COMMENT_SIZE = {
  height: 40,
  width: 40,
};
export const CommentMarker = ({
  scaledComment,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onPendingClick,
}: {
  scaledComment: ScaledComment;
  isSelected: boolean;
  onSelect: (id: number | null) => void;
  onEdit: (comment: AnnotationFeedbackDetail) => void;
  onDelete: (id: number) => void;
  onPendingClick: () => void;
}) => {
  const { x, y, comment } = scaledComment;
  const hasAuthor = isAnnotationFeedbackDetail(comment);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = useCallback(() => {
    if (hasAuthor) {
      onSelect(isSelected ? null : comment.id);
    } else {
      onPendingClick();
    }
  }, [hasAuthor, isSelected, comment.id, onSelect, onPendingClick]);

  const handleEdit = useCallback(() => {
    if (hasAuthor) {
      onEdit(comment);
      onSelect(null);
    }
  }, [hasAuthor, comment, onEdit, onSelect]);

  const handleDelete = useCallback(() => {
    onDelete(comment.id);
  }, [comment.id, onDelete]);

  const commonSx = {
    left: x,
    position: 'absolute' as const,
    top: y,
    transform: 'translate(-5%, -100%)',
  };

  if (hasAuthor) {
    return (
      <>
        <Box key={comment.id} ref={setAnchorEl} sx={commonSx} data-testid="comment-marker">
          <SpeechBubbleAvatar fullName={comment.author.full_name} onClick={handleClick} />
        </Box>
        {isSelected ? (
          <CommentBubble
            comment={comment}
            anchorEl={anchorEl}
            open={isSelected}
            onClose={() => onSelect(null)}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : null}
      </>
    );
  }

  return (
    <ChatBubble
      data-testid="pending-comment"
      fontSize="large"
      onClick={handleClick}
      sx={{
        ...commonSx,
        color: 'white',
      }}
    />
  );
};
